import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faMobile } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { StatusCode } from "api/protocols";
import { PostEmailCampaignDraftPreview } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import type { IEmailCampaignDesignStatus } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import { EmailBuilderPreviewFrame } from "pages/secure/facility/marketing/emailBuilder/shell/preview/EmailBuilderPreviewFrame";
import { EmailBuilderPreviewLoadingFrame } from "pages/secure/facility/marketing/emailBuilder/shell/preview/EmailBuilderPreviewLoadingFrame";
import { EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX } from "pages/secure/facility/marketing/emailBuilder/shell/preview/emailBuilderPreviewConstants";
import type { TEmailBuilderPreviewViewport } from "redux/reducers/emailBuilder/emailBuilder";
import "pages/secure/facility/marketing/emailBuilder/shell/preview/emailBuilderPreview.scss";

interface IProps {
  campaignId: number;
  design?: IEmailCampaignDesignStatus | null;
}

type TPreviewStatus = "loading" | "ready" | "failed" | "unavailable";

const PREVIEW_VIEWPORT_OPTIONS: Array<{
  viewport: TEmailBuilderPreviewViewport;
  labelKey: "Desktop" | "Mobile";
  icon: typeof faDesktop;
}> = [
    { viewport: "desktop", labelKey: "Desktop", icon: faDesktop },
    { viewport: "mobile", labelKey: "Mobile", icon: faMobile },
  ];

function emailCampaignHasPreviewableDesign(design?: IEmailCampaignDesignStatus | null): boolean {
  if (design == null) {
    return false;
  }

  return design.has_draft || design.active_published_design_id != null;
}

export default function EmailCampaignPreview(props: IProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TPreviewStatus>("loading");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewViewport, setPreviewViewport] = useState<TEmailBuilderPreviewViewport>("desktop");

  const showViewportToggle = emailCampaignHasPreviewableDesign(props.design);

  useEffect(() => {
    if (props.campaignId < 1 || !emailCampaignHasPreviewableDesign(props.design)) {
      setStatus("unavailable");
      setPreviewHtml(null);
      setPreviewError(null);
      return;
    }

    let cancelled = false;

    setStatus("loading");
    setPreviewHtml(null);
    setPreviewError(null);

    void (async () => {
      const res = await PostEmailCampaignDraftPreview(props.campaignId, false);

      if (cancelled) {
        return;
      }

      if (res.status !== StatusCode.OK || res.data?.data?.html == null) {
        setStatus("failed");
        setPreviewError(
          typeof res.message === "string" && res.message.length > 0
            ? res.message
            : t("Could not load email preview."),
        );
        return;
      }

      setPreviewHtml(res.data.data.html);
      setStatus("ready");
    })();

    return () => {
      cancelled = true;
    };
  }, [props.campaignId, props.design?.has_draft, props.design?.active_published_design_id, t]);

  return (
    <div
      className={classNames(
        "email-builder-preview",
        "relative rounded-2xl border bg-primary-25",
        `email-builder-preview--${previewViewport}`,
      )}
    >
      {showViewportToggle && (
        <div
          className="absolute top-4 right-4 z-10 inline-flex items-stretch overflow-hidden border border-gray-300 rounded-lg bg-white"
        >
          {PREVIEW_VIEWPORT_OPTIONS.map((option, index) => {
            const selected = option.viewport === previewViewport;
            const isLast = index === PREVIEW_VIEWPORT_OPTIONS.length - 1;

            return (
              <button
                key={option.viewport}
                className={classNames(
                  "inline-flex h-9 w-10 cursor-pointer items-center justify-center border-r border-gray-300 p-0 text-gray-700",
                  selected && "bg-gray-100 text-gray-900",
                  !selected && "bg-white hover:bg-gray-500",
                  isLast && "border-r-0",
                )}
                onClick={() => setPreviewViewport(option.viewport)}
              >
                <FontAwesomeIcon icon={option.icon} aria-hidden="true" />
              </button>
            );
          })}
        </div>

      )}
      <div className="email-builder-preview-body">
        {status === "unavailable" && (
          <div className="email-builder-preview-status">
            {t("Design the content of your email to preview it here.")}
          </div>
        )}

        {status === "loading" && (
          <EmailBuilderPreviewLoadingFrame
            previewViewport={previewViewport}
            contentWidthPx={EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX}
          />
        )}

        {status === "failed" && (
          <div className="email-builder-preview-status email-builder-preview-status--error">
            {previewError ?? t("Could not load email preview.")}
          </div>
        )}

        {status === "ready" && previewHtml === null && (
          <div className="email-builder-preview-status email-builder-preview-status--error">
            {t("Could not load email preview.")}
          </div>
        )}

        {status === "ready" && previewHtml !== null && (
          <EmailBuilderPreviewFrame
            previewHtml={previewHtml}
            previewViewport={previewViewport}
            contentWidthPx={EMAIL_BUILDER_PREVIEW_CONTENT_WIDTH_PX}
            iframeTitle={t("Email preview")}
          />
        )}
      </div>
    </div>
  );
}
